import { useState, useEffect, useCallback } from 'react';
import { ScannerData } from '../types/ScannerData';
import { getApiUrl } from '../config/api';

/** YYYY-MM-DD in America/New_York for `now` (locale-agnostic). */
function formatEtYmd(now: Date): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(now);
}

/** Minutes since midnight in America/New_York. */
function etMinutesSinceMidnight(now: Date): number {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const h = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10);
  const m = parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10);
  return h * 60 + m;
}

/** Subtract whole calendar days from a YYYY-MM-DD string (UTC calendar math). */
function addCalendarDaysYmd(ymd: string, deltaDays: number): string {
  const [y, mo, d] = ymd.split('-').map((x) => parseInt(x, 10));
  const u = new Date(Date.UTC(y, mo - 1, d + deltaDays));
  const yy = u.getUTCFullYear();
  const mm = String(u.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(u.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

// Trading date label (YYYYMMDD): ET calendar; before 9:30 or after 16:00 ET → previous calendar day
const getCurrentTradingDate = (): string => {
  const now = new Date();
  const ymd = formatEtYmd(now);
  const mins = etMinutesSinceMidnight(now);
  const open = 9 * 60 + 30;
  const close = 16 * 60;
  const calendar =
    mins < open || mins >= close ? addCalendarDaysYmd(ymd, -1) : ymd;
  return calendar.replace(/-/g, '');
};

interface HistoricalDataHook {
  selectedDate: string | null;
  setSelectedDate: (date: string | null) => void;
  selectedTimestamp: string | null;
  setSelectedTimestamp: (timestamp: string | null) => void;
  availableDates: string[];
  availableTimestamps: string[];
  isLoading: boolean;
  error: string | null;
  fetchAvailableDates: () => Promise<void>;
  fetchAvailableTimestamps: (date: string) => Promise<void>;
  fetchHistoricalData: (date: string, timestamp?: string) => Promise<ScannerData[] | null>;
  isHistoricalMode: boolean;
  currentTradingDate: string;
}

export const useHistoricalData = (): HistoricalDataHook => {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTimestamp, setSelectedTimestamp] = useState<string | null>(null);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [availableTimestamps, setAvailableTimestamps] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isHistoricalMode = selectedDate !== null;

  // Debug logging - DISABLED for performance
  // console.log('🔍 [HISTORICAL HOOK DEBUG]', {
  //   selectedDate,
  //   selectedTimestamp,
  //   isHistoricalMode,
  //   availableDatesLength: availableDates.length,
  //   availableTimestampsLength: availableTimestamps.length
  // });

  const fetchAvailableDates = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const url = getApiUrl('/api/historical-snapshots');
      const response = await fetch(url);
      if (!response.ok) {
        if (response.status === 404) {
          setAvailableDates([]);
          return;
        }
        setAvailableDates([]);
        setError('Historical snapshots not available');
        return;
      }
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        setAvailableDates([]);
        return;
      }
      const data = await response.json();
      if (data.success) {
        setAvailableDates(data.available_dates || []);
      } else {
        setAvailableDates([]);
        setError(data.error || 'Failed to fetch available dates');
      }
    } catch (err) {
      setAvailableDates([]);
      setError(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchAvailableTimestamps = useCallback(async (date: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const url = getApiUrl(`/api/historical-snapshots/${date}/timestamps`);
      const response = await fetch(url);
      if (!response.ok) {
        setAvailableTimestamps([]);
        return;
      }
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        setAvailableTimestamps([]);
        return;
      }
      const data = await response.json();
      if (data.success) {
        setAvailableTimestamps(data.available_timestamps || []);
      } else {
        setAvailableTimestamps([]);
      }
    } catch (err) {
      setAvailableTimestamps([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchHistoricalData = useCallback(async (date: string, timestamp?: string): Promise<ScannerData[] | null> => {
    try {
      setIsLoading(true);
      setError(null);
      const path = timestamp
        ? `/api/historical-snapshots/${date}/${timestamp}`
        : `/api/historical-snapshots/${date}`;
      const url = getApiUrl(path);
      const response = await fetch(url);
      if (!response.ok) return null;
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) return null;
      const data = await response.json();
      if (data.success) return data.data || [];
      setError(data.error || `Failed to fetch data for ${date}` + (timestamp ? ` at ${timestamp}` : ''));
      return null;
    } catch (err) {
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load available dates on mount
  useEffect(() => {
    fetchAvailableDates();
    // FORCE RESET: Ensure we start with current data (no historical date selected)
    setSelectedDate(null);
    setSelectedTimestamp(null);
  }, [fetchAvailableDates]);

  // Load available timestamps when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableTimestamps(selectedDate);
      setSelectedTimestamp(null); // Reset timestamp when date changes
    } else {
      setAvailableTimestamps([]);
      setSelectedTimestamp(null);
    }
  }, [selectedDate, fetchAvailableTimestamps]);

  return {
    selectedDate,
    setSelectedDate,
    selectedTimestamp,
    setSelectedTimestamp,
    availableDates,
    availableTimestamps,
    isLoading,
    error,
    fetchAvailableDates,
    fetchAvailableTimestamps,
    fetchHistoricalData,
    isHistoricalMode,
    currentTradingDate: getCurrentTradingDate()
  };
};
